import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { TenantService } from 'src/app/tenant/tenant.service';
import { UserMessageComponent } from 'src/app/shared/messages/user-message/user-message.component';

@Component({
  selector: 'app-email-verification-reminder',
  standalone: true,
  imports: [CommonModule, RouterModule, UserMessageComponent],
  template: `
    <app-user-message *ngIf="email && !verified && !dismissed" type="info" title="Please verify your email address" class="email-reminder" [dismissible]="false">
      <div>{{ email }}</div>
      <div class="email-actions">
        <button type="button" class="btn btn-link" [disabled]="sending || cooldown" (click)="resend()">{{ sending ? 'Sending…' : cooldown ? 'Link sent' : 'Resend link' }}</button>
        <a [routerLink]="profileLink" fragment="email" class="btn btn-link">Change email</a>
        <button type="button" class="btn btn-link" (click)="dismissed = true">Dismiss</button>
      </div>
      <small role="status">{{ message }}</small>
    </app-user-message>`,
  styles: [`
    .email-reminder { display:block; max-width:56rem; margin:1rem auto; overflow-wrap:anywhere; }
    .email-actions { display: flex; gap: .5rem; flex-wrap: wrap; }
    .email-actions .btn { padding: .4rem .15rem; font-size: .85rem; color: inherit; text-decoration: underline; }
    @media (max-width: 600px) { .email-reminder { margin: .75rem; } }
  `]
})
export class EmailVerificationReminderComponent implements OnInit, OnDestroy {
  email = '';
  verified = true;
  dismissed = false;
  sending = false;
  cooldown = false;
  message = '';
  private userId = '';
  private subscriptions = new Subscription();
  private request?: Subscription;
  private resendRequest?: Subscription;
  private timer?: ReturnType<typeof setTimeout>;

  constructor(private auth: AuthService, private http: HttpClient, private router: Router, private tenantService: TenantService) {}

  get profileLink(): string[] {
    return ['/', this.tenantService.getTenant(), 'myprofile'];
  }

  ngOnInit() {
    this.subscriptions.add(this.auth.user.subscribe(user => {
      const nextId = user?.id || '';
      if (nextId !== this.userId) {
        this.resetResend();
        this.dismissed = false;
        this.message = '';
        this.email = '';
        this.verified = true;
      }
      this.userId = nextId;
      this.refresh();
    }));
    this.subscriptions.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) this.refresh();
    }));
  }

  private refresh() {
    this.request?.unsubscribe();
    if (!this.userId) return;
    this.request = this.http.get<{ email: string; emailConfirmed: boolean }>(environment.apiHost + '/User/EmailVerificationStatus').subscribe({
      next: status => {
        if (this.email !== status.email) { this.resetResend(); this.dismissed = false; this.message = ''; }
        this.email = status.email;
        this.verified = status.emailConfirmed;
      },
      error: () => { /* A reminder must never block navigation. */ }
    });
  }

  private resetResend() {
    this.resendRequest?.unsubscribe();
    clearTimeout(this.timer);
    this.sending = false;
    this.cooldown = false;
  }

  resend() {
    if (this.sending || this.cooldown) return;
    this.sending = true;
    this.resendRequest = this.http.post(environment.apiHost + '/User/resendActivationEmail?UserId=' + encodeURIComponent(this.userId), null).subscribe({
      next: () => {
        this.sending = false;
        this.cooldown = true;
        this.message = 'Verification link sent. Please check your inbox and spam folder.';
        this.timer = setTimeout(() => this.cooldown = false, 60000);
      },
      error: () => { this.sending = false; this.message = 'Unable to resend right now. Please try again shortly.'; }
    });
  }

  ngOnDestroy() {
    this.resetResend();
    this.subscriptions.unsubscribe();
    this.request?.unsubscribe();
    clearTimeout(this.timer);
  }
}
